import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { useLocalization } from "../../hooks/useLocalization";

export default function TrainingDashboardPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewTraining } = usePermissions();
  const [stats, setStats] = useState(null);

  const CARDS = [
    { key: "awaiting_day1", label: t("mgmtTraining.awaitingDay1"), to: "/management/training/assignments" },
    { key: "onboarding_in_progress", label: t("mgmtTraining.onboardingInProgress"), to: "/management/training/assignments" },
    { key: "quizzes_overdue", label: t("mgmtTraining.quizzesOverdue"), to: "/management/training/assignments" },
    { key: "failed_quizzes", label: t("mgmtTraining.retrainingQuizFailed"), to: "/management/training/assignments" },
    { key: "practical_reviews_pending", label: t("mgmtTraining.practicalReviewsPending"), to: "/management/training/practical-reviews" },
    { key: "retraining_required", label: t("mgmtTraining.retrainingRequired"), to: "/management/training/assignments" },
    { key: "certifications_expiring", label: t("mgmtTraining.certificationsExpiring"), to: "/management/training/certifications" },
    { key: "courses_awaiting_review", label: t("mgmtTraining.coursesAwaitingReview"), to: "/management/training/courses" },
    { key: "sections_awaiting_approval", label: t("mgmtTraining.sectionsAwaitingApproval"), to: "/management/training/content-review" },
  ];

  const load = useCallback(() => {
    if (canViewTraining) trainingApi.dashboard().then((r) => setStats(r.data)).catch(() => {});
  }, [canViewTraining]);
  useEffect(() => { load(); }, [load]);
  useRouteRefresh(load);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="training-access-denied">{t("common.accessDenied")}</p>;

  const NAV_ITEMS = [
    ["sources", t("mgmtTraining.sources"), "/management/training/sources"],
    ["content-review", t("mgmtTraining.contentReview"), "/management/training/content-review"],
    ["courses", t("mgmtTraining.courses"), "/management/training/courses"],
    ["assignments", t("mgmtTraining.assignments"), "/management/training/assignments"],
    ["practical-reviews", t("mgmtTraining.practicalReviews"), "/management/training/practical-reviews"],
    ["certifications", t("mgmtTraining.certifications"), "/management/training/certifications"],
  ];

  return (
    <div className="space-y-5" data-testid="training-dashboard-page">
      <PageHelpBanner pageKey="training-dashboard" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTraining.dashboardHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTraining.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTraining.subtitle")}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {NAV_ITEMS.map(([key, label, to]) => (
          <button key={to} data-testid={`training-nav-${key}`} onClick={() => navigate(to)}
            className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 bg-white">
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {CARDS.map((c) => (
          <button key={c.key} data-testid={`training-stat-${c.key}`} onClick={() => navigate(c.to)}
            className="bg-white border border-gray-200 rounded-xl p-4 text-left hover:border-blue-400 transition-colors">
            <p className="text-3xl font-bold text-gray-900">{stats ? stats[c.key] ?? 0 : "-"}</p>
            <p className="text-xs text-gray-500 mt-1">{c.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
